import React from 'react';
import PropTypes from 'prop-types';
import { Badge, Button, Input, CustomInput, Collapse, Table } from 'reactstrap';
import { calculateItemHistoricDelta, getItemTransactions } from '../../../helpers/transferStatusCalculator';
import { receivingItemFormatter } from '../../../helpers/dataFormatters';

class TransferItemRow extends React.Component {
    state = {
        historyExpanded: false,
        dangerMode: false,
        manualDelta: '',
        product: null,
        productError: null
    };

    componentDidMount() {
        this._fetchProduct();
    }

    _fetchProduct() {
        const { item, productsClient, isRemoved } = this.props;
        if (isRemoved || !productsClient) return;

        productsClient.getProductById(item.product.id)
            .then(product => this.setState({ product }))
            .catch(productError => this.setState({ productError }));
    }

    _toggleHistory = () => {
        this.setState(prev => ({ historyExpanded: !prev.historyExpanded }));
    };

    _toggleDangerMode = () => {
        this.setState(prev => ({
            dangerMode: !prev.dangerMode,
            manualDelta: ''
        }));
    };

    _handleManualDeltaChange = (e) => {
        this.setState({ manualDelta: e.target.value });
    };

    _handleDirectionToggle = () => {
        const { item, onDirectionChange, onSelectChange, isSelected } = this.props;
        onDirectionChange(item.product.id);
        // Auto-select when direction is toggled
        if (!isSelected) {
            onSelectChange(item.product.id, true);
        }
    };

    _handleCheckboxChange = (e) => {
        const { item, onSelectChange } = this.props;
        onSelectChange(item.product.id, e.target.checked);
    };

    _getManualDelta = () => {
        const { manualDelta } = this.state;
        const parsed = parseInt(manualDelta, 10);
        return isNaN(parsed) ? null : parsed;
    };

    resetDangerMode = () => {
        this.setState({ dangerMode: false, manualDelta: '' });
    };

    render() {
        const {
            item,
            transactions,
            isSelected,
            transferDirection,
            isRemoved
        } = this.props;
        const { historyExpanded, dangerMode, manualDelta, product } = this.state;

        // Get piShopSku from fetched product data
        const piShopSku = product && product.key && product.key.piShopSku;

        const itemTransactions = getItemTransactions(item.product.id, transactions);
        const historicDelta = calculateItemHistoricDelta(item.product.id, transactions);
        const transactionCount = itemTransactions.length;

        const rowClass = isRemoved ? 'table-secondary text-muted' : '';

        return (
            <>
                <tr className={rowClass}>
                    {/* Selection Checkbox */}
                    <td className="text-center align-middle">
                        {!dangerMode && (
                            <CustomInput
                                type="checkbox"
                                id={`select-${item.product.id}`}
                                checked={isSelected}
                                onChange={this._handleCheckboxChange}
                            />
                        )}
                    </td>

                    {/* KID */}
                    <td className="align-middle">
                        K{item.product.id}
                        {isRemoved && (
                            <Badge color="warning" className="ml-2" title="Item no longer in PO">
                                Removed
                            </Badge>
                        )}
                    </td>

                    {/* PS SKU */}
                    <td className="align-middle">
                        {piShopSku || <span className="text-muted">N/A</span>}
                    </td>

                    {/* Product Name */}
                    <td className="align-middle">{item.product.name}</td>

                    {/* Quantity */}
                    <td className="text-center align-middle">{item.quantity}</td>

                    {/* Receiving Status */}
                    <td className="text-center align-middle">
                        {isRemoved || !item.receivingTotals ? (
                            <span className="text-muted">N/A</span>
                        ) : (
                            receivingItemFormatter(item, null, true)
                        )}
                    </td>

                    {/* Historic Delta */}
                    <td className="text-center align-middle">
                        <span className={historicDelta < 0 ? 'text-danger' : historicDelta > 0 ? 'text-success' : ''}>
                            {historicDelta}
                        </span>
                    </td>

                    {/* Direction / Danger Mode */}
                    <td className="align-middle">
                        {dangerMode ? (
                            <div className="d-flex align-items-center">
                                <Input
                                    type="number"
                                    placeholder="Delta"
                                    value={manualDelta}
                                    onChange={this._handleManualDeltaChange}
                                    style={{ width: '80px' }}
                                    className="mr-2"
                                />
                                <Button
                                    size="sm"
                                    color="secondary"
                                    onClick={this._toggleDangerMode}
                                >
                                    Cancel
                                </Button>
                            </div>
                        ) : (
                            <div className="d-flex align-items-center">
                                <CustomInput
                                    type="switch"
                                    id={`direction-${item.product.id}`}
                                    label={transferDirection === 'toSaturn' ? 'To Saturn' : 'To Marketplace'}
                                    checked={transferDirection === 'toSaturn'}
                                    onChange={this._handleDirectionToggle}
                                    className="mr-2"
                                />
                            </div>
                        )}
                    </td>

                    {/* Actions */}
                    <td className="align-middle">
                        <div className="d-flex">
                            {!dangerMode && (
                                <Button
                                    size="sm"
                                    color="danger"
                                    outline
                                    onClick={this._toggleDangerMode}
                                    className="mr-1"
                                    title="Manual delta entry"
                                >
                                    Danger
                                </Button>
                            )}
                            {transactionCount > 0 && (
                                <Button
                                    size="sm"
                                    color="info"
                                    outline
                                    onClick={this._toggleHistory}
                                >
                                    <Badge color="info" pill className="mr-1">
                                        {transactionCount}
                                    </Badge>
                                    {historyExpanded ? 'Hide' : 'History'}
                                </Button>
                            )}
                        </div>
                    </td>
                </tr>

                {/* Expandable Transaction History */}
                {historyExpanded && transactionCount > 0 && (
                    <tr>
                        <td colSpan="9" className="bg-light p-0">
                            <Collapse isOpen={historyExpanded}>
                                <div className="p-3">
                                    <h6>Transaction History</h6>
                                    <Table size="sm" bordered>
                                        <thead>
                                            <tr>
                                                <th>Date</th>
                                                <th>Delta</th>
                                                <th>PO Qty</th>
                                                <th>Response</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {itemTransactions.map((tx, idx) => (
                                                <tr key={idx}>
                                                    <td>{new Date(tx.timestamp).toLocaleString()}</td>
                                                    <td className={tx.delta < 0 ? 'text-danger' : 'text-success'}>
                                                        {tx.delta}
                                                    </td>
                                                    <td>{tx.poQty}</td>
                                                    <td>
                                                        <small>
                                                            <code>{JSON.stringify(tx.saturnResponse)}</code>
                                                        </small>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Collapse>
                        </td>
                    </tr>
                )}
            </>
        );
    }
}

TransferItemRow.propTypes = {
    item: PropTypes.object.isRequired,
    transactions: PropTypes.array.isRequired,
    isSelected: PropTypes.bool.isRequired,
    transferDirection: PropTypes.oneOf(['toMarketplace', 'toSaturn']).isRequired,
    isRemoved: PropTypes.bool,
    onSelectChange: PropTypes.func.isRequired,
    onDirectionChange: PropTypes.func.isRequired,
    productsClient: PropTypes.object
};

TransferItemRow.defaultProps = {
    isRemoved: false
};

export { TransferItemRow };
